import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton, Avatar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getVerificationDetails } from "../../../services/authAPI";

type Props = {
  open: boolean;
  onClose: () => void;
  profile: any;
  calculateAge: (dob?: string) => string | number;
  getInitial: (name?: string) => string;
  isAdminView: boolean;
};

type VerificationResponse = {
  verification?: {
    id_proof_front_url?: string | null;
    id_proof_back_url?: string | null;
    payment_receipt_url?: string | null;
  };
};

const ProfileDetailsModal: React.FC<Props> = ({ open, onClose, profile, calculateAge, getInitial, isAdminView }) => {
  const [verification, setVerification] = useState<VerificationResponse | null>(null);

  useEffect(() => {
    const loadVerification = async () => {
      try {
        const result = await getVerificationDetails(profile.id);

        setVerification(result);
      } catch (error) {
        console.error(error);
      }
    };

    if (profile?.id) {
      loadVerification();
    }
  }, [profile?.id]);

  if (!profile) return null;
  const formatDate = (dob: string) => (dob ? new Date(dob).toLocaleDateString("en-GB") : "-");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
          },
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle className='modal-header'>
        <span>Full Profile Details</span>

        <IconButton onClick={onClose} className='close-btn'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className='modal-body'>
        {/* ================= HERO ================= */}
        <div className='modal-hero'>
          <Avatar className='profile-avatar' src={profile.photo_url || undefined}>
            {!profile.photo_url && getInitial(profile.name || profile.email)}
          </Avatar>

          <div className='profile-hero-info'>
            <h2>{profile.name}</h2>
            <h4>Profile Id-: {profile.id}</h4>
          </div>
        </div>

        {/* ================= ALL SECTIONS (UNCHANGED) ================= */}

        <div className='modal-section'>
          <h4>Basic Details</h4>
          <div className='grid'>
            <div>
              <b>Gender:</b> {profile.gender}
            </div>
            <div>
              <b>Age:</b>
              {calculateAge(profile.dob)} yrs
            </div>
            <div>
              <b>DOB:</b> {formatDate(profile.dob)}
            </div>
            <div>
              <b>Birth Time:</b> {profile.birth_time}
            </div>
            <div>
              <b>Mother Tongue:</b> {profile.mother_tongue}
            </div>
            <div>
              <b>Profile Created By:</b> {profile.profile_created_by}
            </div>
            <div>
              <b>Marital Status:</b> {profile.marital_status}
            </div>
            <div>
              <b>Children:</b> {profile.children}
            </div>
            <div>
              <b>Kids Live With:</b> {profile.kids_live_with}
            </div>
          </div>
        </div>

        <div className='modal-section'>
          <h4>Religion & Astrology</h4>
          <div className='grid'>
            <div>
              <b>Religion:</b> {profile.religion}
            </div>
            <div>
              <b>Caste:</b> {profile.caste}
            </div>
            <div>
              <b>Zodiac:</b> {profile.zodiac}
            </div>
            <div>
              <b>Star:</b> {profile.star}
            </div>
            <div>
              <b>Star Patham:</b> {profile.star_patham}
            </div>
            <div>
              <b>Kiraga Paavam:</b> {profile.kiraga_paavam}
            </div>
            <div>
              <b>Sevvai Lakna:</b> {profile.sevvai_lakna}
            </div>
          </div>
        </div>

        <div className='modal-section'>
          <h4>Physical Details</h4>
          <div className='grid'>
            <div>
              <b>Body Type:</b> {profile.body_type}
            </div>
            <div>
              <b>Physical Status:</b> {profile.physical_status}
            </div>
            <div>
              <b>Height:</b> {profile.height}
            </div>
            <div>
              <b>Weight:</b> {profile.weight}
            </div>
            <div>
              <b>Complexion:</b> {profile.complexion}
            </div>
          </div>
        </div>

        <div className='modal-section'>
          <h4>Location</h4>
          <div className='grid'>
            <div>
              <b>Birth Country:</b> {profile.birth_country}
            </div>
            <div>
              <b>Resident Country:</b> {profile.resident_country}
            </div>
            <div>
              <b>City:</b> {profile.city}
            </div>
          </div>
        </div>

        <div className='modal-section'>
          <h4>Education & Career</h4>
          <div className='grid'>
            <div>
              <b>Education:</b> {profile.education}
            </div>
            <div>
              <b>Occupation:</b> {profile.occupation}
            </div>
          </div>
        </div>

        <div className='modal-section'>
          <h4>Family</h4>
          <div className='grid'>
            <div>
              <b>Father:</b> {profile.father_place}
            </div>
            <div>
              <b>Mother:</b> {profile.mother_place}
            </div>
            <div>
              <b>Father Caste:</b> {profile.father_caste}
            </div>
            <div>
              <b>Mother Caste:</b> {profile.mother_caste}
            </div>
            <div>
              <b>Father Job:</b> {profile.father_occupation}
            </div>
            <div>
              <b>Mother Job:</b> {profile.mother_occupation}
            </div>
          </div>
        </div>

        <div className='modal-section'>
          <h4>Lifestyle</h4>
          <div className='grid'>
            <div>
              <b>Hobbies:</b> {profile.hobbies}
            </div>
            <div>
              <b>Eating:</b> {profile.eating_habit}
            </div>
            <div>
              <b>Drinking:</b> {profile.drinking_habit}
            </div>
            <div>
              <b>Smoking:</b> {profile.smoking_habit}
            </div>
          </div>
        </div>

        <div className='modal-section'>
          <h4>About</h4>
          <p className='about-text'>{profile.about || "No information available"}</p>
        </div>

        <div className='modal-section'>
          <h4>Partner Preferences</h4>
          <p className='about-text'>{profile.partner_qualities || "No partner preference specified"}</p>
        </div>

        <div className='modal-section'>
          <h4>Documents</h4>
          <div className='grid'>
            {/* HOROSCOPE */}
            <h3>Horoscope</h3>
            {profile.horoscope_url ? (
              <img src={profile.horoscope_url} alt='Horoscope' className='horoscope-img' />
            ) : (
              <p className='empty-text'>No horoscope uploaded</p>
            )}
          </div>
          
        </div>
        {isAdminView && (
          <div className='profile-card documents-card'>
            <div className='documents-header'>
              <h3>Verification Documents</h3>
            </div>

            <div className='documents-grid'>
              <div className='document-card'>
                <h4>ID Proof Front</h4>

                {verification?.verification?.id_proof_front_url ? (
                  <img src={verification.verification.id_proof_front_url} alt='ID Front' className='document-img' />
                ) : (
                  <div className='document-placeholder'>Not Uploaded</div>
                )}
              </div>

              <div className='document-card'>
                <h4>ID Proof Back</h4>

                {verification?.verification?.id_proof_back_url ? (
                  <img src={verification.verification.id_proof_back_url} alt='ID Back' className='document-img' />
                ) : (
                  <div className='document-placeholder'>Not Uploaded</div>
                )}
              </div>

              <div className='document-card'>
                <h4>Payment Receipt</h4>

                {verification?.verification?.payment_receipt_url ? (
                  <img
                    src={verification.verification.payment_receipt_url}
                    alt='Payment Receipt'
                    className='document-img'
                  />
                ) : (
                  <div className='document-placeholder'>Not Uploaded</div>
                )}
              </div>
            </div>
            <div className='modal-section'>
              <h4>Admin view details</h4>
              <div className='grid'>
                <div>
                  <b>Address:</b> {profile.address || "Not Provided"}
                </div>
                <div>
                  <b>Email:</b> {profile.email || "Not Provided"}
                </div>
              </div>
              <div className='grid'>
            {/* HOROSCOPE */}
            <h3>Photo</h3>
            {profile.photo_url ? (
              <img src={profile.photo_url} alt='Profile' className='horoscope-img' />
            ) : (
              <p className='empty-text'>No photo uploaded</p>
            )}
          </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileDetailsModal;
